// The ownership scan behind the patient-delete guard (COLAB-394).
//
// ### Why this read lives in the session package
//
// It asks which clinicians own the sessions linked to a set of patients. That
// is a session read filtered by a patient id, the same shape as its neighbour
// `getSessionsByPatientProfileIdV2_2` in `../api/list`. The consumer is the
// patient delete confirm dialog, but `@heidi/patients` cannot import this door
// without opening a `patients → session` edge, which this ticket is not the
// right vehicle for. The app composition point carries the result across as a
// prop.
//
// ### Relation to BE enforcement
//
// Today this FE scan IS the enforcement for the "no delete over a teammate's
// linked session" rule. COLAB-343 (merged) narrows the delete endpoint to
// `managed_by == caller OR workspace admin`, which is patient-level only, and
// never looks at who owns the linked sessions. The session-ownership refusal
// is the scope of **COLAB-445**: it extends `_caller_may_delete`'s non-admin
// arm with a session-ownership check and widens the admin cascade to clean
// every linked session regardless of owner. Until COLAB-445 lands, mobile and
// direct-API clients bypass this guard and the delete goes through at BE.
//
// After COLAB-445 ships, this FE scan becomes a UX pre-flight: it explains the
// refusal in the dialog BEFORE the request fires rather than a generic error
// toast after, and BE catches anything the FE verdict missed.
//
// The guard reports pending on an unresolved caller so the confirm action
// stays disabled on data that cannot be compared yet. It fails open on error
// and truncation because fail-closed would invent a block the FE cannot
// clear even when BE would permit the delete; see `design.md` Decision 5
// for the trade-off, including the gap this leaves until COLAB-445 ships.
//
// ### Why the caller's identity is a parameter
//
// `currentKindeUserId` is passed in rather than resolved here, so this package
// gains no identity coupling and no new dependency. It is also the only way to
// keep the cache identity free of the caller — the queryFn returns raw
// (patient, owner) pairs and the comparison happens outside it.
//
// ### The comparison is against `kinde_user_id`, never `user_ref_id`
//
// A session's `kinde_gp_id` is a Kinde IDP id. `PatientProfile.managed_by` is a
// Mongo ObjectId, surfaced on the user record as `user_ref_id`. They are
// different strings for the same person, so a caller that reuses the id
// `usePatientProfileDeleteContext` resolves (`user_ref_id ?? kinde_user_id`)
// makes every comparison mismatch — and every patient holding any session then
// looks blocked. `useSessionOwner` is the precedent for the correct pairing.

import {
  SessionScope,
  SessionSortBy,
  Shared__enums__enums__SortOrder as SortOrder,
} from '@heidi/schema/ml-scribe';
import { skipToken, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';

import { foreignSessionOwnersKeys } from '../../contracts';
import { getSessionsListV2 } from '../api/list';
import { getSessionDataConfig } from '../config';

/**
 * One page is the whole scan. The v2.2 list is cursor-paginated and carries no
 * total, so "are any of these sessions foreign" cannot be answered by counting
 * — only by reading pages. Reading every page of a heavily-used patient to
 * decide whether to render a warning is not a trade this surface should make,
 * so the scan stops after one page and reports the truncation instead.
 */
const FOREIGN_SESSION_SCAN_PAGE_SIZE = 200;

interface SessionOwnership {
  patientProfileId: string | undefined;
  ownerKindeUserId: string;
}

interface ForeignSessionScan {
  ownership: SessionOwnership[];
  /** True when more linked sessions exist than the single scanned page. */
  isTruncated: boolean;
}

export interface UseQueryForeignSessionOwnersArgs {
  /** The patient profiles to scan. Order is irrelevant — the key sorts. */
  patientProfileIds: readonly string[];
  /** The caller's `kinde_user_id`. NOT `user_ref_id` — see module header. */
  currentKindeUserId: string | undefined;
  /**
   * Callers gate this themselves: admins bypass the guard, and so does a
   * workspace where the shared-directory gate is not on. Both must issue no
   * request at all, which is why this is a required argument rather than an
   * option with a permissive default.
   */
  enabled: boolean;
}

export interface ForeignSessionOwnersResult {
  /** True when at least one scanned session is owned by another clinician. */
  hasForeignSession: boolean;
  /** True while the scan is in flight. False whenever the scan is disabled. */
  isPending: boolean;
}

/**
 * Reports whether any session linked to `patientProfileIds` is owned by a
 * clinician other than the caller.
 *
 * Reports pending when the caller identity is unresolved so the confirm
 * action stays disabled on data that cannot be compared yet. Fails open on
 * error and truncation: fail-closed would invent a block the FE cannot clear
 * even when BE would permit the delete, so a verdict that cannot be computed
 * does not block. A foreign session found inside the scanned page is still
 * conclusive — only the unscanned remainder is treated as unknown.
 *
 * `staleTime: 0` keeps the verdict fresh across remounts (reopen the dialog,
 * reopen the drawer): a cached "clear" could otherwise outlive a teammate
 * linking a session to the patient. `refetchOnWindowFocus: false` prevents a
 * tab-back from scanning again while the dialog is open and flipping confirm
 * to disabled mid-selection; freshness-on-open is already covered by the
 * dialog remount running against `staleTime: 0`.
 *
 * On a refetch error over cached data the cached verdict is preserved, so a
 * known foreign block does not drop silently while the next refetch tries.
 */
export function useQueryForeignSessionOwners({
  patientProfileIds,
  currentKindeUserId,
  enabled,
}: UseQueryForeignSessionOwnersArgs): ForeignSessionOwnersResult {
  // `enabled && !currentKindeUserId` is pending-until-known rather than
  // disabled: the comparison cannot be made yet, so reporting "verified clear"
  // would be a lie that resolves to false a moment later.
  const hasSelection = patientProfileIds.length > 0;
  const isActive = enabled && !!currentKindeUserId && hasSelection;
  const isAwaitingCaller = enabled && !currentKindeUserId && hasSelection;

  const query = useQuery({
    queryKey: foreignSessionOwnersKeys.forProfiles(patientProfileIds),
    enabled: isActive,
    staleTime: 0,
    // Tab-back would otherwise refetch under `staleTime: 0`, flip the dialog's
    // confirm back to disabled mid-selection and swap the radios for the
    // pending state while the scan returns.
    refetchOnWindowFocus: false,
    queryFn: isActive
      ? async (): Promise<ForeignSessionScan> => {
          const { data } = await getSessionsListV2({
            scope: SessionScope.all,
            patient_profile_ids: [...patientProfileIds],
            page_size: FOREIGN_SESSION_SCAN_PAGE_SIZE,
            // Pinned so the scanned page is deterministic rather than
            // whatever the server defaults to — an arbitrary page would make
            // the guard non-reproducible for a patient past the page size.
            sort_by: SessionSortBy.created_at,
            sort_order: SortOrder.DESC,
          });

          const isTruncated = data.next_cursor != null;
          if (isTruncated) {
            getSessionDataConfig().trackLinkedSessionsSignal?.(
              'foreign_session_scan_truncated',
              { flag_on: true, page_size: FOREIGN_SESSION_SCAN_PAGE_SIZE }
            );
          }

          return {
            ownership: data.sessions.map((s) => ({
              patientProfileId: s.patient_profile_id ?? undefined,
              ownerKindeUserId: s.kinde_gp_id,
            })),
            isTruncated,
          };
        }
      : skipToken,
  });

  const { data, isError, errorUpdatedAt, isLoading, isFetching } = query;

  // Emit the scan-failed signal once per settled error, not once per attempt.
  // RQ's `retry` option fans one failure into N queryFn calls, so putting the
  // emit inside the queryFn would multiply the event; the queryFn also fires
  // on every refocus/remount under `staleTime: 0`. `errorUpdatedAt` ticks once
  // per settled failure, so keying the effect on it fires exactly once per
  // event — and twice when a transient refetch error settles into another one.
  const lastEmittedAt = useRef<number>(0);
  useEffect(() => {
    if (!isError || errorUpdatedAt === 0) return;
    if (errorUpdatedAt === lastEmittedAt.current) return;
    lastEmittedAt.current = errorUpdatedAt;
    getSessionDataConfig().trackLinkedSessionsSignal?.('foreign_session_scan_failed', {
      flag_on: true,
    });
  }, [isError, errorUpdatedAt]);

  return useMemo(() => {
    // Pending while enabled but the caller has not resolved yet, OR the
    // underlying query is loading / fetching for a fresh answer. A disabled
    // react-query reports `pending` indefinitely, so pending is derived rather
    // than read off `query.isPending` to avoid freezing the confirm action on
    // every path the caller deliberately skipped.
    const isPending = isAwaitingCaller || (isActive && (isLoading || isFetching));

    // Preserve a known foreign verdict on refetch error. A refetch that fails
    // over cached `data` must not drop a block the previous read proved — if
    // the cache says foreign, the UI stays blocked while the next refetch
    // tries.
    if (!isActive || !data) {
      return { hasForeignSession: false, isPending };
    }

    // A foreign session is decisive whether or not the response also carries
    // its `patient_profile_id`. `SessionListItemView.patient_profile_id` is
    // `string | null` and the schema documents null as "profile-resolve service
    // was unavailable (graceful degrade)" — so a row scoped to our filter can
    // still come back without the id. `.some(…)` reads the ownership rows
    // directly rather than filtering an id list first, which would silently
    // drop a null-id foreign row and fail open on a real refusal case.
    const hasForeignSession = data.ownership.some((row) =>
      isForeignOwner(row.ownerKindeUserId, currentKindeUserId)
    );

    return { hasForeignSession, isPending };
  }, [isActive, isAwaitingCaller, isLoading, isFetching, data, currentKindeUserId]);
}

/**
 * A session counts as foreign only when both ids are known and differ.
 *
 * `SessionListItemView.kinde_gp_id` is a required `string` in the schema. The
 * only "unknown" value that reaches here is the empty string, which this
 * function treats as absence and therefore NOT foreign — treating absence as
 * "someone else" would block on missing data, the fail-closed direction this
 * guard deliberately avoids. An unknown CALLER is the same case from the
 * other side; there is nothing to compare against, so nothing can be
 * established.
 */
function isForeignOwner(
  ownerKindeUserId: string,
  currentKindeUserId: string | undefined
): boolean {
  if (!ownerKindeUserId || !currentKindeUserId) return false;
  return ownerKindeUserId !== currentKindeUserId;
}
